// features/organization/sitesLocations/SiteLocationsPage.jsx

import { useState, useEffect } from "react";
import apiClient from "../../../shared/API/apiClient";
import SiteModal from "./SiteModal";
import DeleteModal from "../../../shared/components/DeleteModal";
import Toast from "../../../shared/components/Toast";
import Pagination from "../../../shared/components/Pagination";
import usePagination from "../../../shared/hooks/usePagination";
import { data } from "react-router-dom";

const SiteLocations = () => {
  const [legalEntities, setLegalEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");

  const [siteOptions, setSiteOptions] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");

  const [sites, setSites] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [toast, setToast] = useState(null);
  const user = JSON.parse(localStorage.getItem("user") ?? "{}");
  // const isAdmin = user?.role_name === "Admin";

  const showToast = (message, type = "success") => setToast({ message, type });

  // ── Pagination ──────────────────────────────────────
  const {
    currentPage,
    totalPages,
    pageData,
    goToPage,
    setPageSize,
    pageSize,
    totalItems,
    startIndex,
    endIndex,
  } = usePagination(sites, 10);
  // ────────────────────────────────────────────────────

  const fetchSites = async () => {
    setLoadingList(true);
    try {
      const params = {};

      if (selectedEntity) {
        params.legal_entity_id = selectedEntity;
      }

      if (selectedSite) {
        params.site_id = selectedSite;
      }

      const res = await apiClient.get("/api/sites-locations", {
        params,
      });
      setSites(res.data.data ?? []);
    } catch (err) {
      console.error("Failed to fetch sites:", err);
      showToast(
        err?.response?.data?.message || "Failed to load entities",
        "error"
      );
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, [selectedEntity, selectedSite]);

  const fetchLegalEntities = async () => {
    try {
      const res = await apiClient.get("/api/legal-entities");

      if (res.data?.code === 200) {
        setLegalEntities(res.data.data || []);
      }
    } catch (err) {
      console.error("Legal entity fetch error:", err);
    }
  };

  useEffect(() => {
    fetchLegalEntities();
  }, []);

  const fetchSiteOptions = async () => {
    try {
      const params = {};

      if (selectedEntity) {
        params.legal_entity_id = selectedEntity;
      }

      const res = await apiClient.get("/api/sites-locations", {
        params,
      });

      if (res.data?.code === 200) {
        setSiteOptions(res.data.data || []);
      } else {
        setSiteOptions([]);
      }
    } catch (err) {
      console.error("Site options fetch error:", err);
      setSiteOptions([]);
    }
  };

  useEffect(() => {
    fetchSiteOptions();
    setSelectedSite("");
  }, [selectedEntity]);

  const openAdd = () => {
    setEditData(null);
    setShowModal(true);
  };
  const openEdit = (site) => {
    setEditData(site);
    setShowModal(true);
  };
  const close = () => {
    setShowModal(false);
    setEditData(null);
  };

  const handleSaved = (saved, wasEdit, response) => {
    fetchSites();
    showToast(
      response?.message ||
        (wasEdit ? "Site updated successfully!" : "Site added successfully!"),
      "success"
    );
    close();
  };

  const handleDeleted = (id, response) => {
    setSites((prev) => prev.filter((s) => s.site_id !== id));
    setDeleteRecord(null);
    showToast(response?.message || "Site deleted successfully.", "delete");
  };

  return (
    <div>
      <div className="dash-page">
        <div className="le-page-header">
          <div>
            <div className="dash-page-title">Site / Location Management</div>
            <div className="dash-page-subtitle">
              Operational sites included in BRSR reporting boundary
            </div>
          </div>
          <br></br>
          <button className="org-btn-save le-add-btn" onClick={openAdd}>
            + &nbsp;Add Location
          </button>
        </div>

        <div className="filter-card">
          {/* Legal Entity */}
          <div className="filter-group">
            <label>Legal Entity</label>

            <select
              className="filter-input"
              value={selectedEntity}
              onChange={(e) => setSelectedEntity(e.target.value)}
            >
              <option value="">All Legal Entities</option>

              {legalEntities.map((entity) => (
                <option key={entity.entity_id} value={entity.entity_id}>
                  {entity.entity_name}
                </option>
              ))}
            </select>
          </div>

          {/* Site Name */}
          <div className="filter-group">
            <label>Site Name</label>

            <select
              className="filter-input"
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
            >
              <option value="">All Sites</option>

              {siteOptions.map((site) => (
                <option key={site.site_id} value={site.site_id}>
                  {site.site_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="le-card">
          {loadingList ? (
            <div className="le-empty">
              <div className="le-empty-text">Loading sites…</div>
            </div>
          ) : sites.length === 0 ? (
            <div className="le-empty">
              <div className="le-empty-text">No sites added yet.</div>
              <button className="le-empty-btn" onClick={openAdd}>
                + &nbsp;Add your first site
              </button>
            </div>
          ) : (
            <>
              <div className="table-wrap">
                <table className="le-table">
                  <thead>
                    <tr>
                      <th>Site Name</th>
                      <th>Site Type</th>
                      <th>Linked Entity</th>
                      <th>Address</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((s, i) => (
                      <tr key={s.site_id}>
                        <td data-label="Site Name" className="le-entity-name">
                          {s.site_name}
                        </td>
                        <td data-label="Site Type">{s.site_type || "—"}</td>
                        {/* <td data-label="Department">{s.department_name || "—"}</td> */}
                        <td data-label="Linked Entity">
                          {s.entity_name || "—"}
                        </td>
                        <td data-label="Address">{s.address || "—"}</td>
                        <td data-label="Actions">
                          <div className="le-actions">
                            <span
                              className="action-item edit"
                              onClick={() => openEdit(s)}
                            >
                              <img
                                src="https://cdn-icons-png.flaticon.com/512/1828/1828911.png"
                                alt="edit"
                                className="action-icon"
                              />
                              Edit
                            </span>

                            <span
                              className="action-item delete"
                              onClick={() => setDeleteRecord(s)}
                            >
                              <img
                                src="https://cdn-icons-png.flaticon.com/512/1214/1214428.png"
                                alt="delete"
                                className="action-icon"
                              />
                              Delete
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination bar */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                startIndex={startIndex}
                endIndex={endIndex}
                onPageChange={goToPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </div>
      </div>

      {showModal && (
        <SiteModal onClose={close} onSaved={handleSaved} editData={editData} />
      )}

      {deleteRecord && (
        <DeleteModal
          record={deleteRecord}
          idKey="site_id"
          nameKey="site_name"
          endpoint="/api/sites-locations"
          onClose={() => setDeleteRecord(null)}
          onDeleted={handleDeleted}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default SiteLocations;
